import {
  Backdrop,
  Button,
  Grid,
  IconButton,
  Switch,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import axios from "axios";
import { setSnackbar } from "../../../../../components/hooks/snackBar/reducers";
import { Delete, EditNote } from "@mui/icons-material";
import _ from "lodash";
import CustomDataGrid from "../../../../../components/dataTable/customDataGrid";
import CustomTextField from "../../../../../components/utils/input/customTextField";
import Modal from "../../../../../components/utils/modal/modal";
import DeletePage from "../../../../../components/utils/deleteModal/deletePage";
import Loader from "../../../../../components/loader/loader";
import { Helmet } from "react-helmet-async";
import CustomdropDown from "../../../../../components/utils/dropDown/customdropDown";

const TransactionProvider = () => {
  const dispatch = useDispatch();

  const pageOption = [10, 25, 50, 100];
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [pageCount, setPageCount] = useState(0);
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [active, setActive] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [isLoader, setIsLoader] = useState(false);
  const [outletData, setOutletData] = useState([]);

  const validationSchema = yup.object({
    transaction_provider: yup.string().required("Required"),
    merchant_id: yup.string().required("Required"),
    merchant_key: yup.string().required("Outlet required"),
    url: yup.string().url("Invalid URL").required("Required"),
    storeid: yup.string().notRequired(),
    terminalid: yup.string().notRequired(),
  });

  const formik = useFormik({
    initialValues: {
      transaction_provider: "",
      merchant_id: "",
      merchant_key: "",
      url: "",
      storeid: "",
      terminalid: "",
    },
    validationSchema,
    onSubmit: isEdit ? handleUpdate : handleCreate,
  });

  const onCreate = () => {
    setOpen(true);
    formik.resetForm();
    setActive(true);
    setIsEdit(false);
    setActiveId(null);
  };

  function handleCreate() {
    const payload = {
      transaction_provider: formik.values.transaction_provider,
      merchant_id: formik.values.merchant_id,
      merchant_key: formik.values.merchant_key,
      url: formik.values.url,
      storeid: formik.values.storeid,
      terminalid: formik.values.terminalid,
      // is_active: active, // only if backend accepts it
    };

    setIsLoader(true);
    axios
      .post("/v1/transaction_provider", payload)
      .then(() => {
        setOpen(false);
        getTransactionProviders();
        dispatch(setSnackbar(true, "success", "Created Successfully"));
      })
      .catch((err) => {
        dispatch(
          setSnackbar(
            true,
            "error",
            err?.response?.data?.errors?.[0]?.message || "Error while creating",
          ),
        );
      })
      .finally(() => setIsLoader(false));
  }

  function onEdit(row) {
    formik.setFieldValue("transaction_provider", row.transaction_provider);
    formik.setFieldValue("merchant_id", row.merchant_id);
    formik.setFieldValue("merchant_key", row.merchant_key);
    formik.setFieldValue("url", row.url);
    formik.setFieldValue("storeid", row.storeid || "");
    formik.setFieldValue("terminalid", row.terminalid || "");
    setActive(row.is_active);
    setIsEdit(true);
    setOpen(true);
    setActiveId(row.id);
  }

  function handleUpdate() {
    const payload = {
      transaction_provider: formik.values.transaction_provider,
      merchant_id: formik.values.merchant_id,
      merchant_key: formik.values.merchant_key,
      url: formik.values.url,
      storeid: formik.values.storeid,
      terminalid: formik.values.terminalid,
      // is_active: active, // only if backend accepts it in PUT schema
    };

    setIsLoader(true);
    axios
      .put(`/v1/transaction_provider/${activeId}`, payload)
      .then(() => {
        setOpen(false);
        getTransactionProviders();
        dispatch(setSnackbar(true, "success", "Updated Successfully"));
      })
      .catch((err) => {
        dispatch(
          setSnackbar(
            true,
            "error",
            err?.response?.data?.errors?.[0]?.message || "Error while updating",
          ),
        );
      })
      .finally(() => setIsLoader(false));
  }

  function onDelete(id) {
    setOpenDialog(true);
    setActiveId(id);
  }

  const handleDelete = () => {
    setIsLoader(true);
    axios
      .delete(`/v1/transaction_provider/${activeId}`)
      .then(() => {
        setOpenDialog(false);
        setOpen(false);
        getTransactionProviders();
        dispatch(setSnackbar(true, "success", "Deleted Successfully"));
      })
      .catch((err) => {
        dispatch(
          setSnackbar(
            true,
            "error",
            err?.response?.data?.errors?.[0]?.message || "Error while deleting",
          ),
        );
      })
      .finally(() => setIsLoader(false));
  };

  const getOutletData = () => {
    setIsLoader(true);
    axios
      .get("/v1/outlet/list/1")
      .then((res) => setOutletData(res.data || []))
      .catch((err) =>
        dispatch(setSnackbar(true, "error", "Error fetching outlets")),
      )
      .finally(() => setIsLoader(false));
  };

  const columns = [
    {
      field: "sno",
      headerName: "S.no",
      minWidth: 70,
      maxWidth: 100,
      headerAlign: "center",
      align: "center",
      sortable: false,
    },
    {
      field: "transaction_provider",
      headerName: "Provider",
      minWidth: 180,
      headerAlign: "center",
      align: "center",
      sortable: false,
    },
    {
      field: "merchant_id",
      headerName: "Merchant ID",
      minWidth: 180,
      headerAlign: "center",
      align: "center",
      sortable: false,
    },
    {
      field: "merchant_key",
      headerName: "Outlet",
      minWidth: 220,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const outlet = outletData.find(
          (o) => o.id.toString() === params.row.merchant_key,
        );
        return outlet ? outlet.fullname : params.row.merchant_key;
      },
    },

    {
      field: "url",
      headerName: "URL",
      minWidth: 220,
      headerAlign: "center",
      align: "center",
      sortable: false,
    },
    {
      field: "is_active",
      headerName: "Status",
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => (
        <Switch checked={params.row.is_active} disabled />
      ),
    },
    {
      field: "storeid",
      headerName: "Store ID",
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      sortable: false,
    },
    {
      field: "terminalid",
      headerName: "Terminal ID",
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      sortable: false,
    },

    {
      field: "action",
      headerName: "Action",
      minWidth: 100,
      headerAlign: "center",
      align: "center",
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      disableExport: true,
      renderCell: (params) => (
        <div className="flex justify-center w-full">
          <IconButton onClick={() => onEdit(params.row)}>
            <EditNote sx={{ color: "#1972d2", fontSize: 22 }} />
          </IconButton>
          <IconButton onClick={() => onDelete(params.id)}>
            <Delete sx={{ color: "red", fontSize: 22 }} />
          </IconButton>
        </div>
      ),
    },
  ];

  const rows = _.map(data, (val, idx) => ({
    id: val.id,
    sno: ++idx,
    transaction_provider: val.transaction_provider,
    merchant_id: val.merchant_id,
    merchant_key: val.merchant_key,
    outlet_name: val.outlet_name,
    url: val.url,
    storeid: val.storeid,
    terminalid: val.terminalid,
    is_active: val.is_active,
  }));

  const filteredRows = search
    ? _.map(data, (val, idx) => ({
        id: val.id,
        sno: ++idx,
        transaction_provider: val.transaction_provider,
        merchant_id: val.merchant_id,
        merchant_key: val.merchant_key,
        outlet_name: val.outlet_name,
        url: val.url,
        storeid: val.storeid,
        terminalid: val.terminalid,
        is_active: val.is_active,
      })).filter((row) =>
        Object.values(row).some(
          (val) =>
            val != null &&
            String(val).toLowerCase().includes(search.toLowerCase()),
        ),
      )
    : rows;

  const gridProps = {
    // rows,
    rows: filteredRows,
    columns,
    search,
    setSearch,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    pageCount,
    pageOption,
  };

  const getTransactionProviders = () => {
    setIsLoader(true);
    axios
      .get("/v1/transaction_provider")
      .then((res) => {
        setData(res.data || []);
        setPageCount((res.data || []).length);
      })
      .catch((err) => {
        dispatch(
          setSnackbar(
            true,
            "error",
            err?.response?.data?.errors?.[0]?.message ||
              "Error while fetching transaction providers",
          ),
        );
      })
      .finally(() => setIsLoader(false));
  };

  useEffect(() => {
    getTransactionProviders();
  }, []);

  useEffect(() => {
    getTransactionProviders();
    getOutletData();
  }, []);

  return (
    <Fragment>
      <Helmet>
        <title>Transaction Provider</title>
      </Helmet>

      <Typography variant="h5">Transaction Provider</Typography>

      {!open && (
        <Grid className="flex justify-end">
          <Button variant="contained" size="small" onClick={onCreate}>
            Create New
          </Button>
        </Grid>
      )}

      {open && (
        <Modal
          onClose={setOpen}
          onSave={formik.handleSubmit}
          formik={formik}
          isEdit={isEdit}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={4} lg={3}>
              <CustomTextField
                id="transaction_provider"
                value={formik.values.transaction_provider}
                title="Provider"
                placeholder="Enter provider name"
                onchange={formik.handleChange}
                errorMsg={
                  formik.touched.transaction_provider &&
                  Boolean(formik.errors.transaction_provider)
                }
                helperText={
                  formik.touched.transaction_provider &&
                  formik.errors.transaction_provider
                }
              />
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <CustomTextField
                id="merchant_id"
                value={formik.values.merchant_id}
                title="Merchant ID"
                placeholder="Enter merchant ID"
                onchange={formik.handleChange}
                errorMsg={
                  formik.touched.merchant_id &&
                  Boolean(formik.errors.merchant_id)
                }
                helperText={
                  formik.touched.merchant_id && formik.errors.merchant_id
                }
              />
            </Grid>

            <Grid item xs={12} md={4} lg={3}>
              <CustomdropDown
                id="merchant_key"
                data={outletData}
                optionLabel={(option) => option.fullname}
                value={
                  outletData.find(
                    (o) => o.id.toString() === formik.values.merchant_key,
                  ) || null
                }
                onchange={(e, value) => {
                  formik.setFieldValue(
                    "merchant_key",
                    value?.id?.toString() || "",
                  );
                }}
                optionEqual={(option, value) => option.id === value?.id}
                placeholder="Select Outlet"
                disabled={outletData.length === 0}
                helperText={
                  outletData.length === 0
                    ? "No outlets available"
                    : formik.touched.merchant_key && formik.errors.merchant_key
                }
                errorMsg={
                  formik.touched.merchant_key &&
                  Boolean(formik.errors.merchant_key)
                }
                title="Outlet"
              />
            </Grid>

            <Grid item xs={12} md={4} lg={3}>
              <CustomTextField
                id="url"
                value={formik.values.url}
                title="URL"
                placeholder="Enter provider URL"
                onchange={formik.handleChange}
                errorMsg={formik.touched.url && Boolean(formik.errors.url)}
                helperText={formik.touched.url && formik.errors.url}
              />
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <CustomTextField
                id="storeid"
                value={formik.values.storeid}
                title="Store ID"
                placeholder="Enter store id"
                onchange={formik.handleChange}
                errorMsg={
                  formik.touched.storeid && Boolean(formik.errors.storeid)
                }
                helperText={formik.touched.storeid && formik.errors.storeid}
              />
            </Grid>

            <Grid item xs={12} md={4} lg={3}>
              <CustomTextField
                id="terminalid"
                value={formik.values.terminalid}
                title="Terminal ID"
                placeholder="Enter terminal id"
                onchange={formik.handleChange}
                errorMsg={
                  formik.touched.terminalid && Boolean(formik.errors.terminalid)
                }
                helperText={
                  formik.touched.terminalid && formik.errors.terminalid
                }
              />
            </Grid>

            <Grid item xs={12} md={4} lg={3} className="flex items-center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={active}
                    onChange={() => setActive(!active)}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </Modal>
      )}

      <Grid className="bg-white rounded-md p-2 my-3">
        <CustomDataGrid props={gridProps} />
      </Grid>

      <Backdrop open={openDialog} sx={{ zIndex: 1350 }}>
        <DeletePage onCancel={setOpenDialog} onSubmit={handleDelete} />
      </Backdrop>

      <Loader isLoader={isLoader} />
    </Fragment>
  );
};

export default TransactionProvider;
